import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { commentApi } from "../../backend/client";
import { Bubble, BubbleContent } from "../../components/ui/bubble";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";

type ProjectComment = {
  id: string | number;
  author_id: string;
  body: string;
  created_at: string;
};

type ProjectCommentsProps = {
  projectId: string | number;
  currentUser?: { id?: string } | null;
};

const fmt = (timestamp: string) =>
  new Date(timestamp).toLocaleString(undefined, { hour12: false });

export default function ProjectComments({
  projectId,
  currentUser,
}: ProjectCommentsProps) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ProjectComment[]>([]);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectComment | null>(
    null,
  );
  const [errorText, setErrorText] = useState("");

  const canSave = useMemo(
    () => body.trim().length > 0 && !saving,
    [body, saving],
  );

  const loadComments = useCallback(async () => {
    if (!projectId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setErrorText("");

    const { data, error } = await commentApi.listForProject(projectId);
    if (error) {
      setErrorText(error.message || "Failed to load comments");
      setLoading(false);
      return;
    }

    setRows(
      (data || []).sort(
        (left, right) =>
          new Date(left.created_at).getTime() -
          new Date(right.created_at).getTime(),
      ),
    );
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  async function saveComment() {
    if (!canSave) return;
    setSaving(true);
    setErrorText("");

    try {
      const { error } = await commentApi.createForProject(projectId, {
        body: body.trim(),
      });
      if (error) throw error;
      setBody("");
      await loadComments();
    } catch (error) {
      setErrorText(
        error instanceof Error ? error.message : "Failed to save comment",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row: ProjectComment) {
    if (!currentUser?.id || row.author_id !== currentUser.id) return;

    setDeletingId(row.id);
    setErrorText("");

    try {
      const { error } = await commentApi.delete(row.id);
      if (error) throw error;
      setRows((previous) => previous.filter((item) => item.id !== row.id));
      setPendingDelete(null);
    } catch (error) {
      setErrorText(
        error instanceof Error ? error.message : "Failed to delete comment",
      );
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <section className="space-y-3">
        {errorText && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errorText}
          </div>
        )}

        <div
          className="max-h-[360px] min-h-32 space-y-4 overflow-y-auto rounded-lg bg-muted/35 p-3"
          role="log"
          aria-label="Project notes"
        >
          {loading ? (
            <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading notes
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
              No notes yet.
            </div>
          ) : (
            rows.map((row) => {
              const mine = row.author_id === currentUser?.id;

              return (
                <Bubble
                  key={row.id}
                  align={mine ? "end" : "start"}
                  variant={mine ? "tinted" : "secondary"}
                >
                  <div
                    className={`mb-1 flex items-center gap-2 px-1 text-xs text-muted-foreground ${
                      mine ? "justify-end" : "justify-start"
                    }`}
                  >
                    <span>
                      {mine ? "You" : "Team"} · {fmt(row.created_at)}
                    </span>
                    {mine && (
                      <button
                        type="button"
                        className="font-medium text-muted-foreground underline-offset-4 hover:text-destructive hover:underline"
                        disabled={deletingId === row.id}
                        onClick={() => setPendingDelete(row)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <BubbleContent>{row.body}</BubbleContent>
                </Bubble>
              );
            })
          )}
        </div>

        <div className="relative">
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                void saveComment();
              }
            }}
            placeholder="Add an internal note…"
            rows={3}
            className="min-h-20 resize-none pr-12"
          />
          <Button
            type="button"
            size="icon"
            className="absolute bottom-2 right-2 h-8 w-8 rounded-full"
            onClick={saveComment}
            disabled={!canSave}
            aria-label="Send note"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Internal only · Press{" "}
          {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}
          +Enter to send
        </p>
      </section>

      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this note?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes your note from the project conversation.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingId !== null}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={!pendingDelete || deletingId !== null}
              onClick={() => pendingDelete && void handleDelete(pendingDelete)}
            >
              {deletingId !== null ? "Removing…" : "Remove note"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
